import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import {
  calcDpTotalUtil,
  calcTwoPricesUtil,
  convertCurrencyUtil,
  convertToTargetCurrencyUtil,
  DpPricePart,
  DpTotalResult,
} from './currencyConversion';

// Pure (non-hook) implementations live in ./currencyConversion (no React/Redux
// imports — safe for reducers and unit tests). Re-exported here so existing
// import sites keep working.
export {
  convertEurToUsd,
  convertUsdToEur,
  hasSameCurrency,
  normalizeCurrencySign,
  convertCurrencyUtil,
  calcTwoPricesUtil,
  convertToTargetCurrencyUtil,
  calcDpTotalUtil,
  resolveTotalCurrencySign,
} from './currencyConversion';
export type { DpPricePart, DpTotalResult } from './currencyConversion';

export const useCalcTwoPrices = () => {
  const { rates } = useSelector(
    (state: RootState) => state.config.config.siteConfig
  );
  const calcTwoPrices = ({
    price1,
    curr1,
    price2,
    curr2,
  }: {
    price1: number;
    curr1: string;
    price2: number;
    curr2: string;
  }) => calcTwoPricesUtil({ price1, curr1, price2, curr2, rates });

  const convertToTargetCurrency = ({
    price,
    curr,
    targetCurrency,
  }: {
    price: number;
    curr: string;
    targetCurrency: string;
  }) => convertToTargetCurrencyUtil({ price, curr, targetCurrency, rates });

  const convertCurrency = ({
    amount,
    from,
    to,
  }: {
    amount: number;
    from?: string;
    to?: string;
  }) => convertCurrencyUtil({ amount, from, to, rates });

  const adjustFiltersPriceRangeLabels = ({
    opt_filters,
    flight_price,
    flight_currency,
  }: {
    opt_filters: any;
    flight_price?: number;
    flight_currency?: string;
  }) => {
    if (!flight_price || !flight_currency) {
      return opt_filters;
    }

    return opt_filters.map((filter: any) => {
      if (filter.attr !== 'price') {
        return filter;
      }
      return {
        ...filter,
        adjusted_min_label: calcTwoPrices({
          price1: filter?.min,
          curr1: filter?.unit,
          price2: flight_price,
          curr2: flight_currency,
        }),
        adjusted_max_label: calcTwoPrices({
          price1: filter?.max,
          curr1: filter?.unit,
          price2: flight_price,
          curr2: flight_currency,
        }),
      };
    });
  };

  return {
    adjustFiltersPriceRangeLabels,
    convertToTargetCurrency,
    convertCurrency,
    calcTwoPrices,
  };
};

/**
 * Hook flavor of calcDpTotalUtil — binds `rates` from the Redux store.
 * The single source of truth for a dynamic package total (flight + room +
 * optional event); use it everywhere a DP total is shown or persisted.
 */
export const useCalcDpTotal = () => {
  const { rates } = useSelector(
    (state: RootState) => state.config.config.siteConfig
  );
  return ({
    parts,
  }: {
    parts: (DpPricePart | undefined | null)[];
  }): DpTotalResult => calcDpTotalUtil({ parts, rates });
};

export const getTotalPaxFromMultiRoom = ({
  multi_room_guests,
  adult,
  child,
}: {
  multi_room_guests: {
    adult: number | string;
    child: number | string;
    infant: number | string;
  }[];
  adult: number;
  child: number;
}) => {
  if (Array.isArray(multi_room_guests) && multi_room_guests?.length > 0) {
    let totalCount = 0;
    multi_room_guests.forEach(
      (pax: {
        adult: number | string;
        child: number | string;
        infant: number | string;
      }) => {
        totalCount += +pax.adult;
        totalCount += +pax.child;
      }
    );

    return totalCount;
  }

  return adult + child;
};
